import { useEffect, useState } from "react";

import { Button, Checkbox, Group, Modal, Stack } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";

export interface SignAndLockConfirmOptions {
  closeoutPregnancy?: boolean;
}

interface SignAndLockConfirmModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (options?: SignAndLockConfirmOptions) => Promise<void>;
  title?: React.ReactNode;
  showPregnancyCloseoutOption?: boolean;
}

const titleComponent = (
  <TextWrapper size="xl" ta="center" variant="bold">
    Sign & Lock Encounter?
  </TextWrapper>
);

export default function SignAndLockConfirmModal({
  onClose,
  onConfirm,
  opened,
  title = titleComponent,
  showPregnancyCloseoutOption = false,
}: SignAndLockConfirmModalProps) {
  const [isSigningAndLocking, setIsSigningAndLocking] = useState(false);
  const [closeoutPregnancy, setCloseoutPregnancy] = useState(false);

  useEffect(() => {
    if (!opened) setCloseoutPregnancy(false);
  }, [opened]);

  const signAndLock = async () => {
    if (isSigningAndLocking) return;
    setIsSigningAndLocking(true);
    try {
      await onConfirm(showPregnancyCloseoutOption ? { closeoutPregnancy } : undefined);
    } finally {
      setIsSigningAndLocking(false);
    }
  };

  const handleClose = () => {
    if (isSigningAndLocking) return;
    onClose();
  };

  return (
    <Modal
      centered
      title={title}
      opened={opened}
      onClose={handleClose}
      closeOnEscape={!isSigningAndLocking}
      data-testid="sign-lock-confirm-modal"
      withCloseButton={!isSigningAndLocking}
      closeOnClickOutside={!isSigningAndLocking}
    >
      <Stack>
        <TextWrapper c="gray.6">
          Edits after closing must be made through the amendment process.
        </TextWrapper>
        {showPregnancyCloseoutOption && (
          <Checkbox
            checked={closeoutPregnancy}
            disabled={isSigningAndLocking}
            label="Close out client's pregnancy as well"
            data-testid="sign-lock-closeout-pregnancy-checkbox"
            onChange={(event) => setCloseoutPregnancy(event.currentTarget.checked)}
          />
        )}
        <Group pt="sm" justify="flex-end" mx={{ base: "xs", xs: 0 }}>
          <Button
            bd="burgundy.1"
            variant="outline"
            color="burgundy.9"
            onClick={handleClose}
            disabled={isSigningAndLocking}
            data-testid="sign-lock-cancel-button"
          >
            Cancel
          </Button>
          <Button
            color="white"
            bg="burgundy.9"
            variant="filled"
            onClick={signAndLock}
            loading={isSigningAndLocking}
            data-testid="sign-lock-confirm-button"
          >
            Yes, Sign & Lock
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
